import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import InstrumentTypeFields from "./InstrumentTypeFields";

interface Props {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  availableCredit: number;
  onSuccess: () => void;
}

const RefundCreditModal: React.FC<Props> = ({ show, onHide, enrollmentId, availableCredit, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [instrumentTypeId, setInstrumentTypeId] = useState<number | null>(null);
  const [bankName, setBankName] = useState("");
  const [bankBranchName, setBankBranchName] = useState("");
  const [instrumentDate, setInstrumentDate] = useState("");
  const [instrumentIdentifier, setInstrumentIdentifier] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Keep the modal open after success to show the confirmation; the parent's
  // onSuccess (refresh + close) fires when the user closes.
  const handleClose = () => {
    if (successMsg) onSuccess();
    else onHide();
  };

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setAmount("");
      setRemarks("");
      setInstrumentTypeId(null);
      setBankName("");
      setBankBranchName("");
      setInstrumentDate("");
      setInstrumentIdentifier("");
    }
  }, [show]);

  const handleSubmit = async () => {
    setError(null);
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      setError("Amount must be greater than 0");
      return;
    }
    if (numAmount > availableCredit) {
      setError(`Amount (${numAmount.toLocaleString()}) exceeds available credit (${availableCredit.toLocaleString()})`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await EnrollmentService.refundCredit(enrollmentId, {
        amount: numAmount,
        instrumentTypeId,
        bankName,
        bankBranchName,
        instrumentDate: instrumentDate || null,
        instrumentIdentifier,
        remarks,
      });
      if (res.data?.success) {
        setSuccessMsg(`Refund of ${numAmount.toLocaleString()} processed successfully`);
      } else {
        setError(res.data?.message || "Failed to process refund");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to process refund");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Refund Credit</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        <div style={{ marginBottom: 10, padding: "6px 10px", background: "var(--status-success-bg)", borderRadius: 4, fontSize: 12 }}>
          Available Credit: <b style={{ color: "var(--status-success)" }}>{availableCredit.toLocaleString()}</b>
        </div>

        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>}

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Refund Amount</Form.Label>
          <Form.Control
            type="number"
            size="sm"
            min={1}
            max={availableCredit}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Form.Group>

        <InstrumentTypeFields
          instrumentTypeId={instrumentTypeId}
          onInstrumentTypeChange={setInstrumentTypeId}
          bankName={bankName}
          onBankNameChange={setBankName}
          bankBranchName={bankBranchName}
          onBankBranchNameChange={setBankBranchName}
          instrumentDate={instrumentDate}
          onInstrumentDateChange={setInstrumentDate}
          instrumentIdentifier={instrumentIdentifier}
          onInstrumentIdentifierChange={setInstrumentIdentifier}
        />

        <Form.Group className="mb-2">
          <Form.Label style={{ fontSize: 12 }}>Remarks</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            size="sm"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={handleClose}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <><Spinner role="status" animation="border" size="sm" /> Processing...</> : "Refund Credit"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RefundCreditModal;
